/** One temporary history entry for the active theme-popup stack. */
const marker = 'chemaThemePopup';
const token = `popup-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const detailsSelector = '.header-hamburger, .header-inline-nav__disclosure, currency-picker details, header-drawer details';
const popups = [];
let ownsEntry = false;
let releasing = false;
let traversing = false;
let navigating = false;

function armHistory() {
  if (!popups.length || ownsEntry || releasing || traversing || navigating) return;
  const state = history.state;
  if (state !== null && (typeof state !== 'object' || Array.isArray(state))) return;
  try {
    history.pushState({ ...state, [marker]: token }, '', location.href);
    ownsEntry = true;
  } catch { /* Restricted previews still support Escape and close buttons. */ }
}

function reconcile() {
  if (navigating) return;
  if (popups.length) { armHistory(); return; }
  if (!ownsEntry || traversing) return;
  ownsEntry = false;
  if (history.state?.[marker] !== token) return;
  releasing = true;
  history.back();
}

// Closing a menu link must not start a history traversal that cancels its
// navigation. Resume cleanup if this document is restored from the back cache.
window.addEventListener('click', event => {
  if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
  const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
  if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
  const destination = new URL(link.href, location.href);
  if (!['http:', 'https:'].includes(destination.protocol)) return;
  if (destination.origin === location.origin && destination.pathname === location.pathname && destination.search === location.search && destination.hash) return;
  navigating = true;
});
window.addEventListener('submit', event => {
  const form = event.target;
  if (!event.defaultPrevented && form instanceof HTMLFormElement && form.method !== 'dialog' && (!form.target || form.target === '_self')) navigating = true;
});
window.addEventListener('pageshow', () => { navigating = false; reconcile(); });

function isOpen(element) {
  if (!element.isConnected) return false;
  if (element instanceof HTMLDialogElement || element instanceof HTMLDetailsElement) return element.open;
  try { return element.matches(':popover-open'); }
  catch { return false; }
}

function sync(element) {
  if (!(element instanceof HTMLElement)) return;
  if (!element.matches(`dialog, ${detailsSelector}, [popover]`)) return;
  const index = popups.indexOf(element);
  if (isOpen(element) && index === -1) popups.push(element);
  else if (!isOpen(element) && index !== -1) popups.splice(index, 1);
}

function dismiss(element) {
  if (element.hasAttribute('popover')) return element.hidePopover?.();
  // Use the owning component so animations, scroll unlock and focus recovery
  // complete instead of bypassing them with native dialog.close().
  for (let owner = element.parentElement; owner; owner = owner.parentElement) {
    if (typeof owner.closeDialog === 'function') return owner.closeDialog();
    if (owner.matches('theme-drawer, header-drawer') && typeof owner.close === 'function') return owner.close();
  }
  if (element instanceof HTMLDetailsElement) {
    element.open = false;
    element.querySelector('summary')?.focus({ preventScroll: true });
  } else if (element instanceof HTMLDialogElement) element.close();
  else element.hidePopover?.();
}

function closeTop() {
  const top = popups.at(-1);
  if (!top || top.classList.contains('dialog-closing')) return;
  void Promise.resolve(dismiss(top)).catch(error => console.warn('[popup] Could not close popup', error));
}

window.addEventListener('keydown', event => {
  if (event.key !== 'Escape' || event.isComposing || event.defaultPrevented || !popups.length) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  closeTop();
}, { capture: true });

document.addEventListener('cancel', event => {
  if (!(event.target instanceof HTMLDialogElement)) return;
  sync(event.target);
  event.preventDefault();
  event.stopImmediatePropagation();
  closeTop();
}, { capture: true });

document.addEventListener('toggle', event => { sync(event.target); reconcile(); }, { capture: true });

const observer = new MutationObserver(records => {
  for (const record of records) {
    if (record.type === 'attributes') sync(record.target);
    for (const node of record.addedNodes) {
      if (!(node instanceof Element)) continue;
      sync(node);
      node.querySelectorAll(`dialog[open], details[open], [popover]`).forEach(sync);
    }
  }
  for (let i = popups.length - 1; i >= 0; i--) {
    if (!isOpen(popups[i])) popups.splice(i, 1);
  }
  reconcile();
});
observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open'] });
document.querySelectorAll(`dialog[open], details[open], [popover]`).forEach(sync);
reconcile();

window.addEventListener('popstate', async () => {
  if (releasing) { releasing = false; reconcile(); return; }
  if (history.state?.[marker] === token) {
    // Forward must not revive a dismissed popup or leave an empty guard.
    if (!ownsEntry) {
      const { [marker]: unused, ...state } = history.state;
      try { history.replaceState(state, '', location.href); } catch { /* Restricted preview. */ }
    }
    return;
  }
  if (!ownsEntry) return;
  ownsEntry = false;
  traversing = true;
  const top = popups.at(-1);
  try { if (top) await dismiss(top); }
  catch (error) { console.warn('[popup] Could not close popup', error); }
  finally {
    if (top) sync(top);
    traversing = false;
    reconcile();
  }
});
